import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import useManufacturers from "@/hooks/useManufacturers";
import useProductTypes from "@/hooks/useProductTypes";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Wrench, GraduationCap, Pencil, Search, CheckSquare, Trash2, Check } from "lucide-react";

export function ServiceFormDialog({ open, onOpenChange, initial, isTraining, categories = [], onSaved }) {
  const isEdit = !!initial?.id;
  const mfrs = useManufacturers();
  const types = useProductTypes();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const seed = { ...initial };
      if (Array.isArray(seed.deliverables)) seed.deliverables = seed.deliverables.join("\n");
      setForm(seed);
    } else {
      setForm({ is_training: !!isTraining });
    }
  }, [open, initial, isTraining]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const typesForCompany = types.filter((t) => t.is_active !== false && (!form.manufacturer_id || t.manufacturer_id === form.manufacturer_id));
  const tops = categories.filter((c) => !c.parent_id && c.is_active !== false);
  const noun = isTraining ? "training" : "service";

  const submit = async () => {
    if (!(form.name || "").trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type || null,
        manufacturer_id: form.manufacturer_id || null,
        product_type_id: form.product_type_id || null,
        category_id: form.category_id || null,
        is_training: !!form.is_training,
        description: form.description || null,
        scope: form.scope || null,
        deliverables: typeof form.deliverables === "string" ? form.deliverables.split("\n").map((s) => s.trim()).filter(Boolean) : (form.deliverables || []),
        duration: form.duration || null,
        benefits: form.benefits || null,
        sku: form.sku || null,
        unit_price: form.unit_price !== undefined && form.unit_price !== "" && form.unit_price !== null ? parseFloat(String(form.unit_price).replace(/[^0-9.]/g, "")) || null : null,
        currency: form.currency || "INR",
        uom: form.uom || null,
      };
      if (isEdit) await api.patch(`/services/${initial.id}`, payload);
      else await api.post("/services", payload);
      toast.success(isEdit ? `${noun[0].toUpperCase()}${noun.slice(1)} updated` : `${noun[0].toUpperCase()}${noun.slice(1)} added`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="service-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{isEdit ? `Edit ${noun}` : `New ${noun}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs font-semibold">Name *</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="service-name-input" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs font-semibold">Code / SKU</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs font-semibold">SAC Code</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.sac_code || ""} onChange={(e) => set("sac_code", e.target.value)} placeholder="e.g. 998717" data-testid="service-sac-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs font-semibold">Company (OEM)</Label>
              <Select value={form.manufacturer_id || "__none"} onValueChange={(v) => setForm((f) => ({ ...f, manufacturer_id: v === "__none" ? "" : v, product_type_id: "" }))}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="service-company-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">Unassigned</SelectItem>
                  {mfrs.filter((m) => m.is_active !== false).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Product type</Label>
              <Select value={form.product_type_id || "__none"} onValueChange={(v) => set("product_type_id", v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="service-type-select"><SelectValue placeholder={typesForCompany.length ? "Pick" : "No types"} /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">— None —</SelectItem>
                  {typesForCompany.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Category</Label>
              <Select value={form.category_id || "__none"} onValueChange={(v) => set("category_id", v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="service-category-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">— None —</SelectItem>
                  {tops.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 rounded-2xl border border-border/60 bg-muted/20">
            <div>
              <Label className="text-xs font-semibold">This is a Training programme</Label>
              <p className="text-[10px] text-muted-foreground mt-0.5">Training items appear under the Training tab and Category = Training in quotations.</p>
            </div>
            <input type="checkbox" className="h-5 w-5 accent-primary" checked={!!form.is_training} onChange={(e) => set("is_training", e.target.checked)} data-testid="service-is-training-toggle" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Type (free text)</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.type || ""} onChange={(e) => set("type", e.target.value)} placeholder="e.g. On-site inspection" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Description</Label>
            <Textarea className="rounded-2xl mt-1.5" rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs font-semibold">Deliverables (one per line)</Label>
            <Textarea className="rounded-2xl mt-1.5" rows={3} value={form.deliverables || ""} onChange={(e) => set("deliverables", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs font-semibold">Duration</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.duration || ""} onChange={(e) => set("duration", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Price (₹)</Label>
              <Input type="number" className="rounded-2xl h-11 mt-1.5" value={form.unit_price ?? ""} onChange={(e) => set("unit_price", e.target.value)} data-testid="service-price-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Currency</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.currency || "INR"} onChange={(e) => set("currency", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs font-semibold">UOM</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.uom || ""} onChange={(e) => set("uom", e.target.value)} placeholder={form.is_training ? "Course" : "Service"} />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Benefits</Label>
            <Textarea className="rounded-2xl mt-1.5" rows={2} value={form.benefits || ""} onChange={(e) => set("benefits", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="service-save-button">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Same toolbar + card UI as the Products manager — for Services & Training.
export const ServiceManager = ({ items, isTraining = false, categories = [], canManage, onChanged }) => {
  const mfrs = useManufacturers();
  const types = useProductTypes();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const mfrById = mfrs.reduce((a, m) => { a[m.id] = m; return a; }, {});
  const typeById = types.reduce((a, t) => { a[t.id] = t; return a; }, {});
  const typesForCompany = types.filter((t) => !companyFilter || t.manufacturer_id === companyFilter);
  const activeTypeFilter = typeFilter && typesForCompany.some((t) => t.id === typeFilter) ? typeFilter : "";
  const noun = isTraining ? "training" : "service";
  const Icon = isTraining ? GraduationCap : Wrench;

  const filtered = items
    .filter((s) => !companyFilter || s.manufacturer_id === companyFilter)
    .filter((s) => !activeTypeFilter || s.product_type_id === activeTypeFilter)
    .filter((s) => !catFilter || s.category_id === catFilter)
    .filter((s) => {
      const needle = q.trim().toLowerCase();
      if (!needle) return true;
      return (s.name || "").toLowerCase().includes(needle) || (s.sku || "").toLowerCase().includes(needle) || (s.type || "").toLowerCase().includes(needle);
    })
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} ${noun}${ids.length === 1 ? "" : "s"} permanently?\n\nOld quotations keep their snapshots and are NOT affected.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/services/bulk-delete", { ids });
      toast.success(`${data.deleted} deleted`);
      exitSelectMode();
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setDeleting(false); }
  };

  return (
    <div data-testid={`${noun}-manager`}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code…" className="pl-9 h-10 rounded-2xl bg-muted/40" data-testid={`${noun}-manager-search`} />
        </div>
        <Select value={companyFilter || "__all"} onValueChange={(v) => { setCompanyFilter(v === "__all" ? "" : v); setTypeFilter(""); }}>
          <SelectTrigger className="rounded-2xl h-10 w-[150px]" data-testid={`${noun}-manager-company-filter`}><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All companies</SelectItem>
            {mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={activeTypeFilter || "__all"} onValueChange={(v) => setTypeFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-10 w-[150px]" data-testid={`${noun}-manager-type-filter`}><SelectValue placeholder="Product type" /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All types</SelectItem>
            {typesForCompany.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{!companyFilter && mfrById[t.manufacturer_id] ? ` · ${mfrById[t.manufacturer_id].name}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter || "__all"} onValueChange={(v) => setCatFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-10 w-[150px]" data-testid={`${noun}-manager-category-filter`}><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All categories</SelectItem>
            {categories.filter((c) => !c.parent_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManage && !selectMode && (
          <>
            <Button variant="outline" onClick={() => setSelectMode(true)} className="rounded-2xl" data-testid={`${noun}-select-mode-btn`}>
              <CheckSquare className="h-4 w-4 mr-1" /> Select
            </Button>
            <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-2xl" data-testid={`new-${noun}-button`}>
              <Plus className="h-4 w-4 mr-1" /> Add new
            </Button>
          </>
        )}
        {canManage && selectMode && (
          <>
            <Button variant="outline" onClick={() => setSelected(new Set(filtered.map((s) => s.id)))} className="rounded-2xl" data-testid={`${noun}-select-all-btn`}>
              Select all ({filtered.length})
            </Button>
            <Button variant="outline" onClick={deleteSelected} disabled={!selected.size || deleting} className="rounded-2xl text-red-600 border-red-200 hover:bg-red-50" data-testid={`${noun}-delete-selected-btn`}>
              <Trash2 className="h-4 w-4 mr-1" /> {deleting ? "Deleting…" : `Delete (${selected.size})`}
            </Button>
            <Button variant="ghost" onClick={exitSelectMode} className="rounded-2xl" data-testid={`${noun}-select-cancel-btn`}>Cancel</Button>
          </>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {filtered.map((s) => {
          const isSel = selected.has(s.id);
          return (
            <Card
              key={s.id}
              className={`rounded-3xl shadow-soft ${canManage ? "cursor-pointer card-lift" : ""} ${isSel ? "border-red-300 ring-2 ring-red-200" : "border-border/60"}`}
              onClick={() => canManage && (selectMode ? toggleSelect(s.id) : (setEditing(s), setOpen(true)))}
              data-testid={`${noun}-card-${s.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {selectMode && (
                    <div className={`h-5 w-5 rounded-md border-2 grid place-items-center shrink-0 mt-0.5 ${isSel ? "bg-red-500 border-red-500 text-white" : "border-border"}`}>
                      {isSel && <Check className="h-3.5 w-3.5" />}
                    </div>
                  )}
                  <div className={`h-10 w-10 rounded-2xl grid place-items-center shrink-0 ${isTraining ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-extrabold truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {[s.sku && `Code ${s.sku}`, mfrById[s.manufacturer_id]?.name, typeById[s.product_type_id]?.name, s.type].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                  {s.unit_price ? <p className="font-display font-black text-primary-700 shrink-0">{fmtINR(s.unit_price)}</p> : null}
                </div>
                {s.description && <p className="text-sm text-muted-foreground mt-2.5 line-clamp-2">{s.description}</p>}
                {typeById[s.product_type_id] && (
                  <Badge variant="outline" className="text-[10px] mt-2 bg-primary-50 text-primary-700 border-primary-200">{typeById[s.product_type_id].name}</Badge>
                )}
                {canManage && !selectMode && (
                  <div className="mt-2 pt-2 border-t border-border/40 flex justify-end">
                    <Button variant="ghost" size="sm" className="rounded-xl h-7 text-xs" onClick={(e) => { e.stopPropagation(); setEditing(s); setOpen(true); }} data-testid={`${noun}-edit-${s.id}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <Card className="rounded-3xl border-border/60 shadow-soft sm:col-span-2">
            <CardContent className="p-10 text-center text-sm text-muted-foreground">No {noun}s match.</CardContent>
          </Card>
        )}
      </div>

      <ServiceFormDialog open={open} onOpenChange={setOpen} initial={editing} isTraining={isTraining} categories={categories} onSaved={onChanged} />
    </div>
  );
};
